import {Injectable, Injector} from '@angular/core';
import {HTTP_INTERCEPTORS, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Observable, Subscription} from 'rxjs';
import {
  CHUNK_UPLOAD_THRESHOLD,
  ChunkedUploadService,
  MAX_CHUNK_UPLOAD_FILE_SIZE,
  UploadHandle,
} from '@services/chunked-upload.service';

const TOKEN_FIELD_NAME = '__chunkedUploadTokens';
const RUNDMAIL_ROUTE = '/api/email/sendrundemail';
const ALLOWED_UPLOAD_PREFIXES = [
  '/api/vorlagenverwaltung/',
  '/api/handbuchverwaltung/',
  '/api/haupteinrichtungen/',
  '/api/untereinrichtungen/',
  '/api/externuma/',
  '/api/externanerkennung/',
  '/api/externantragunterschreitung/',
  '/api/externantragverweildauer/',
  '/api/externkind4jhr/',
  '/api/externueberbelegung/',
  '/api/traegerexternmeldungeneoe/',
  '/api/traegerinternmeldungeneoe/saveinternmeldungeoe',
  '/api/traegerinternmeldungeneoe/uebernehmeninternmeldungeoe',
  '/api/traegerexternmeldepflichten/',
  '/api/externantragbeerst/',
  '/api/externantragbeerstnachreichen/',
  '/api/externantragbeerweiterung/',
  '/api/externantragbeerweiterungnachreichen/',
  '/api/externantragbeveraenderung/',
  '/api/externantragbeveraenderungnachreichen/',
];

interface FormDataEntry {
  fieldName: string;
  value: FormDataEntryValue;
  fileIndex: number | null;
}

interface StagedUploadTokenReference {
  uploadToken: string;
  fileIndex: number;
}

@Injectable()
export class ChunkedFormDataInterceptor implements HttpInterceptor {
  constructor(private injector: Injector) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!(request.body instanceof FormData)) {
      return next.handle(request);
    }

    const targetRoute = this.normalizeRoute(request.urlWithParams);
    if (!this.isAllowedRoute(targetRoute)) {
      return next.handle(request);
    }

    const entries = this.readEntries(request.body);
    if (entries.some((entry) => entry.fieldName === TOKEN_FIELD_NAME)) {
      return new Observable((observer) => observer.error(new Error(`Das Formularfeld "${TOKEN_FIELD_NAME}" ist reserviert.`)));
    }

    const files = entries.filter((entry) => entry.fileIndex !== null).map((entry) => entry.value as File);
    const oversizedFile = files.find((file) => file.size > MAX_CHUNK_UPLOAD_FILE_SIZE);
    if (oversizedFile) {
      return new Observable((observer) =>
        observer.error(new Error(`Die Datei "${oversizedFile.name}" überschreitet die maximal zulässige Größe von 100 MiB.`))
      );
    }

    const stageAllFiles = targetRoute === RUNDMAIL_ROUTE;
    if (stageAllFiles && files.reduce((total, file) => total + file.size, 0) > MAX_CHUNK_UPLOAD_FILE_SIZE) {
      return new Observable((observer) =>
        observer.error(new Error('Die Rundmail-Anhänge überschreiten zusammen die maximal zulässige Größe von 100 MiB.'))
      );
    }

    const entriesToStage = entries.filter(
      (entry) => entry.fileIndex !== null && (stageAllFiles || (entry.value as File).size > CHUNK_UPLOAD_THRESHOLD)
    );
    if (entriesToStage.length === 0) {
      return next.handle(request);
    }

    return this.stageAndForward(request, next, targetRoute, entries, entriesToStage);
  }

  private stageAndForward(
    request: HttpRequest<any>,
    next: HttpHandler,
    targetRoute: string,
    entries: FormDataEntry[],
    entriesToStage: FormDataEntry[]
  ): Observable<HttpEvent<any>> {
    return new Observable<HttpEvent<any>>((observer) => {
      const uploadService = this.injector.get(ChunkedUploadService);
      const stagedEntries = new Set(entriesToStage);
      const tokenReferences: StagedUploadTokenReference[] = [];
      let activeUpload: UploadHandle<string> | null = null;
      let fachRequest: Subscription | null = null;
      let cancelled = false;
      let requestCompleted = false;

      const cancelTokens = () => {
        for (const reference of tokenReferences) {
          uploadService.cancelSession(reference.uploadToken);
        }
      };

      const prepareRequest = async () => {
        const formData = new FormData();

        for (const entry of entries) {
          if (stagedEntries.has(entry)) {
            const file = entry.value as File;
            activeUpload = uploadService.stageFile(file, entry.fieldName, targetRoute);
            const uploadToken = await activeUpload.promise;
            activeUpload = null;

            if (cancelled) {
              uploadService.cancelSession(uploadToken);
              return;
            }

            tokenReferences.push({uploadToken, fileIndex: entry.fileIndex as number});
            continue;
          }

          if (typeof entry.value === 'string') {
            formData.append(entry.fieldName, entry.value);
          } else {
            formData.append(entry.fieldName, entry.value, entry.value.name);
          }
        }

        formData.append(TOKEN_FIELD_NAME, JSON.stringify(tokenReferences));
        if (cancelled) {
          cancelTokens();
          return;
        }

        fachRequest = next.handle(request.clone({body: formData})).subscribe({
          next: (event) => observer.next(event),
          error: (error) => {
            cancelTokens();
            observer.error(error);
          },
          complete: () => {
            requestCompleted = true;
            observer.complete();
          },
        });
      };

      prepareRequest().catch((error) => {
        cancelTokens();
        if (!cancelled) {
          observer.error(error);
        }
      });

      return () => {
        cancelled = true;
        activeUpload?.cancel();
        fachRequest?.unsubscribe();
        if (!requestCompleted) {
          cancelTokens();
        }
      };
    });
  }

  private readEntries(formData: FormData): FormDataEntry[] {
    const entries: FormDataEntry[] = [];
    let fileIndex = 0;

    formData.forEach((value, fieldName) => {
      const isFile = value instanceof File;
      entries.push({fieldName, value, fileIndex: isFile ? fileIndex++ : null});
    });

    return entries;
  }

  private normalizeRoute(url: string): string {
    const withoutOrigin = url.replace(/^[a-z][a-z\d+.-]*:\/\/[^/]+/i, '');
    const path = withoutOrigin.split(/[?#]/, 1)[0];
    return (path.startsWith('/') ? path : `/${path}`).replace(/\/+$/, '').toLowerCase();
  }

  private isAllowedRoute(route: string): boolean {
    return route === RUNDMAIL_ROUTE || ALLOWED_UPLOAD_PREFIXES.some((prefix) => route.startsWith(prefix));
  }
}

export const ChunkedFormDataInterceptorProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: ChunkedFormDataInterceptor,
  multi: true,
};
